import { Button } from '@/components/ui/Button';
import { automationService } from '@/services/automation.service';
import type {
  DsnDeclarationItem,
  DsnDeclarationKind,
  DsnDeclarationMonitor as DsnDeclarationMonitorData,
  DsnDeclarationStatus,
} from '@/types/dsn-declaration.types';
import { ChevronDown, CircleAlert, Clock3, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface DsnDeclarationMonitorProps {
  folderUuid: string;
}

const KIND_LABELS: Record<DsnDeclarationKind, string> = {
  monthly: 'DSN mensuelle',
  sick_leave: 'Arrêt de travail',
  contract_end: 'Fin de contrat',
};

const STATUS_CLASSES: Record<DsnDeclarationStatus, string> = {
  accepted: 'text-success-700',
  rejected: 'text-error-700',
  not_generated: 'text-warning-700',
  pending: 'text-neutral-600',
  unknown: 'text-neutral-600',
};

const STATUS_LABELS: Record<DsnDeclarationStatus, string> = {
  accepted: 'Acceptée',
  rejected: 'Rejetée',
  not_generated: 'Non générée',
  pending: 'En attente',
  unknown: 'À contrôler',
};

function readableDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function itemDescription(item: DsnDeclarationItem): string {
  const identity =
    item.employeeDisplayName ||
    (item.employeeMatricule ? `Matricule ${item.employeeMatricule}` : null) ||
    item.establishmentSiret ||
    item.declarationLabel;
  const eventDate = readableDate(item.eventDate);
  return [identity, eventDate, item.eventReason].filter(Boolean).join(' · ');
}

function DeclarationLine({ item }: { item: DsnDeclarationItem }) {
  const description = itemDescription(item);
  return (
    <div className="flex min-h-11 items-start justify-between gap-3 rounded-md px-2 py-2 text-sm">
      <div className="min-w-0">
        <p className="font-medium text-neutral-900">{KIND_LABELS[item.kind]}</p>
        {description && (
          <p className="truncate text-xs text-neutral-500">{description}</p>
        )}
      </div>
      <span
        className={`shrink-0 text-right text-xs font-medium ${STATUS_CLASSES[item.status]}`}
      >
        {STATUS_LABELS[item.status]}
      </span>
    </div>
  );
}

export function DsnDeclarationMonitor({
  folderUuid,
}: DsnDeclarationMonitorProps) {
  const [monitor, setMonitor] = useState<DsnDeclarationMonitorData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const delayedReload = useRef<number | null>(null);
  const loadRequest = useRef(0);
  const currentFolderUuid = useRef(folderUuid);

  const load =
    useCallback(async (): Promise<DsnDeclarationMonitorData | null> => {
      const request = ++loadRequest.current;
      try {
        const result =
          await automationService.getDsnDeclarationMonitor(folderUuid);
        if (request !== loadRequest.current) return null;
        if (!result?.summary || !Array.isArray(result.items)) {
          throw new Error('Réponse de suivi DSN invalide');
        }
        setMonitor(result);
        setError(false);
        return result;
      } catch {
        if (request !== loadRequest.current) return null;
        setError(true);
        return null;
      } finally {
        if (request === loadRequest.current) setLoading(false);
      }
    }, [folderUuid]);

  useEffect(() => {
    currentFolderUuid.current = folderUuid;
    setMonitor(null);
    setLoading(true);
    setRefreshing(false);
    setError(false);
    setMessage(null);
    void load();
    return () => {
      loadRequest.current += 1;
      if (delayedReload.current !== null) {
        window.clearTimeout(delayedReload.current);
        delayedReload.current = null;
      }
    };
  }, [folderUuid, load]);

  const refresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setMessage(null);
    setError(false);
    try {
      const result =
        await automationService.refreshDsnDeclarationMonitor(folderUuid);
      if (currentFolderUuid.current !== folderUuid) return;
      if (result.completed) {
        const latest = await load();
        if (currentFolderUuid.current !== folderUuid) return;
        if (latest?.summary.scanStatus === 'success') {
          setMessage('Liste mise à jour.');
        } else if (latest?.summary.scanStatus === 'partial') {
          setMessage('Vérification terminée avec des résultats partiels.');
        }
      } else {
        setMessage('Vérification lancée. Le résultat apparaîtra ici.');
        delayedReload.current = window.setTimeout(() => void load(), 4_000);
      }
    } catch {
      if (currentFolderUuid.current !== folderUuid) return;
      setError(true);
    } finally {
      if (currentFolderUuid.current === folderUuid) setRefreshing(false);
    }
  };

  const actionable = monitor?.items.filter(item => item.requiresAction) || [];
  const hiddenItems = monitor
    ? [
        ...actionable.slice(4),
        ...monitor.items.filter(item => !item.requiresAction),
      ]
    : [];
  const lastCheckedAt = monitor?.summary.lastCheckedAt
    ? new Date(monitor.summary.lastCheckedAt).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <section
      aria-labelledby="dsn-declaration-monitor-title"
      className="mb-6 border-b border-neutral-100 pb-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3
            id="dsn-declaration-monitor-title"
            className="text-base font-semibold text-neutral-900"
          >
            Déclarations OpenPaye
          </h3>
          <p className="mt-0.5 text-xs text-neutral-500">
            Contrôle le lundi, mercredi et vendredi
            {lastCheckedAt ? ` · Dernier contrôle ${lastCheckedAt}` : ''}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="min-h-11 px-2"
          disabled={refreshing || loading}
          onClick={() => void refresh()}
        >
          <RefreshCw
            aria-hidden="true"
            className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`}
          />
          {refreshing ? 'Vérification…' : 'Vérifier maintenant'}
        </Button>
      </div>

      {message && (
        <p role="status" className="mt-2 text-sm text-neutral-600">
          {message}
        </p>
      )}
      {(error || monitor?.summary.scanStatus === 'error') && (
        <p role="alert" className="text-error-700 mt-2 text-sm">
          OpenPaye est momentanément inaccessible. Le prochain contrôle est
          maintenu.
        </p>
      )}
      {monitor?.summary.scanStatus === 'partial' && !error && (
        <p role="status" className="mt-2 text-sm text-neutral-600">
          Une partie des listes OpenPaye n’a pas répondu. Les autres résultats
          restent disponibles.
        </p>
      )}

      {actionable.length > 0 && (
        <div role="alert" className="bg-error-50 mt-3 rounded-lg px-2 py-2">
          <p className="text-error-700 flex min-h-9 items-center gap-2 px-2 text-sm font-medium">
            <CircleAlert aria-hidden="true" className="h-4 w-4 shrink-0" />
            {actionable.length === 1
              ? '1 déclaration à vérifier'
              : `${actionable.length} déclarations à vérifier`}
          </p>
          {actionable.slice(0, 4).map(item => (
            <DeclarationLine key={item.uuid} item={item} />
          ))}
          {actionable.length > 4 && (
            <p className="px-2 py-1 text-xs text-neutral-600">
              {actionable.length - 4} autre(s) déclaration(s) ci-dessous
            </p>
          )}
        </div>
      )}

      {!loading && monitor && monitor.summary.total === 0 && !error && (
        <p className="mt-3 flex items-center gap-2 text-sm text-neutral-600">
          <Clock3 aria-hidden="true" className="h-4 w-4" />
          Aucune déclaration trouvée sur les deux derniers mois.
        </p>
      )}

      {hiddenItems.length > 0 && (
        <details className="group mt-2">
          <summary className="focus-visible:ring-primary-600 flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-md px-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:outline-none [&::-webkit-details-marker]:hidden">
            <Clock3
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-neutral-500"
            />
            <span className="flex-1">
              Voir l’historique ({hiddenItems.length})
            </span>
            <ChevronDown
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-neutral-400 transition-transform group-open:rotate-180"
            />
          </summary>
          <div className="divide-y divide-neutral-100 pb-1">
            {hiddenItems.map(item => (
              <DeclarationLine key={item.uuid} item={item} />
            ))}
          </div>
        </details>
      )}
    </section>
  );
}
